import { forwardRef } from "react";
import { BiCalendar } from "react-icons/bi";
import { CloseButton, DatePicker, Portal, parseDate } from "@chakra-ui/react";
import { getLocalTimeZone } from "@internationalized/date";
import { InputGroup } from "@/components/ui/input-group";

export interface YearSelectProps {
    isClearable: boolean;
    startYear?: number;
    endYear?: number;
    value?: number | null;
    onChange?: (year: number | null) => void;
    'aria-label'?: string;
}

const toDateValue = (year?: number | null) => {
    if (typeof year !== "number" || Number.isNaN(year)) {
        return [];
    }

    return [parseDate(`${year}-01-01`)];
};

const parseYear = (rawValue: string, fallbackYear?: number | null) => {
    const digitsOnly = rawValue.replace(/\D/g, "").slice(0, 4);
    const parsedYear = Number.parseInt(digitsOnly, 10);

    if (!digitsOnly || Number.isNaN(parsedYear)) {
        return fallbackYear;
    }

    return parsedYear;
};

const YearSelect = forwardRef<HTMLInputElement, YearSelectProps>(
    ({ isClearable, startYear, endYear, value, onChange, 'aria-label': ariaLabel }, ref) => {
        return (
            <DatePicker.Root
                value={toDateValue(value)}
                onValueChange={({ value: nextValue }) => {
                    const year = nextValue[0]?.toDate(getLocalTimeZone()).getFullYear() ?? null;
                    onChange?.(year);
                }}
                format={(date) => date.year.toString()}
                parse={(rawValue) => {
                    const parsedYear = parseYear(rawValue, value);

                    if (typeof parsedYear !== "number") {
                        return undefined;
                    }

                    return parseDate(`${parsedYear}-01-01`);
                }}
                defaultView="year"
                minView="year"
                maxView="year"
                placeholder="yyyy"
                openOnClick
                closeOnSelect
                min={typeof startYear === "number" ? parseDate(`${startYear}-01-01`) : undefined}
                max={typeof endYear === "number" ? parseDate(`${endYear}-01-01`) : undefined}
            >
                <DatePicker.Control asChild>
                    <InputGroup
                        width="full"
                        startElement={<BiCalendar color="blue" />}
                        endElement={
                            isClearable ? (
                                <DatePicker.ClearTrigger asChild>
                                    <CloseButton
                                        me="-3"
                                        aspectRatio="square"
                                        size="2xs"
                                        variant="ghost"
                                        height="calc(100% - {spacing.2})"
                                    />
                                </DatePicker.ClearTrigger>
                            ) : undefined
                        }
                    >
                        <DatePicker.Input
                            ref={ref}
                            aria-label={ariaLabel}
                            textAlign="right"
                        />
                    </InputGroup>
                </DatePicker.Control>
                <Portal>
                    <DatePicker.Positioner>
                        <DatePicker.Content w="auto">
                            <DatePicker.View view="year">
                                <DatePicker.Header />
                                <DatePicker.YearTable columns={3} />
                            </DatePicker.View>
                        </DatePicker.Content>
                    </DatePicker.Positioner>
                </Portal>
            </DatePicker.Root>
        );
    }
);

YearSelect.displayName = "YearSelect";

export default YearSelect;
